// Presentation rules of the registration emails (spec 002, RF-4, RF-8, RF-9). Pure functions, no React:
// every rule (what was registered, why, who may re-invite) lives in the backend; this only words it.

export const NOT_REGISTERED = "not_registered";

const SUPPLIER_NOT_REGISTERED = {
  invitation_not_created:
    "El proveedor se dio de alta, pero no se pudo crear su invitación ni su correo porque la bitácora de auditoría no está disponible. Genera una invitación nueva desde «Invitaciones» cuando el servicio se recupere.",
  default:
    "El proveedor se dio de alta y su invitación existe, pero no se pudo registrar el correo. Genera una invitación nueva desde «Invitaciones» para que el contacto reciba su correo.",
};

const USER_NOT_REGISTERED =
  "El usuario se creó, pero no se pudo registrar su correo de bienvenida. Avísale por otro medio que ya puede entrar con SSO.";

// Notice of an individual alta whose email was not registered, or null when there is nothing to warn about (RF-8).
// `kind` is "user" or "supplier"; `status` and `reason` are what the API answered.
export function altaEmailNotice(kind, status, reason) {
  if (status !== NOT_REGISTERED) return null;
  if (kind === "user") return USER_NOT_REGISTERED;
  return SUPPLIER_NOT_REGISTERED[reason] || SUPPLIER_NOT_REGISTERED.default;
}

// What happened with the email of a freshly generated invitation (RF-4). Returns { tone, text }.
export function invitationNotice(inv) {
  if (inv.already_issued) {
    return {
      tone: "info",
      text: "Ya se generó una invitación hace menos de un minuto (por ejemplo, al dar de alta al proveedor): se muestra la misma y no se envió otro correo. Si necesitas una nueva, inténtalo de nuevo pasado un minuto.",
    };
  }
  const to = inv.contact_email;
  switch (inv.email_status) {
    case "requested":
      return { tone: "ok", text: `Invitación generada. Se envió por correo${to ? ` a ${to}` : ""}; las invitaciones anteriores dejaron de funcionar.` };
    case "duplicate":
      return { tone: "ok", text: "Invitación generada. Su correo ya estaba solicitado, no se envió otro." };
    case NOT_REGISTERED:
      return {
        tone: "warn",
        text: "Invitación generada, pero no se pudo registrar el envío del correo. Copia el link y envíalo manualmente, o genera otra invitación en un minuto.",
      };
    default:
      return { tone: "ok", text: "Invitación generada." };
  }
}

// Rows of a CSV load that were created but whose email was not registered (RF-8). A dry run has none.
export function unregisteredRows(created) {
  return (created || []).filter((row) => row.email_registered === false);
}

const CSV_REASONS = {
  invitation_not_created: "no se creó la invitación (bitácora de auditoría no disponible)",
  request_not_registered: "no se pudo registrar el correo",
};

export function unregisteredRowText(row) {
  const reason = CSV_REASONS[row.email_status_reason] || "no se pudo registrar el correo";
  return `Línea ${row.line}: ${row.name} — ${reason}. Genera una invitación nueva.`;
}

// Why a supplier was left out of a bulk re-invitation (RF-9).
export const SKIP_REASONS = {
  has_account: "ya tiene una cuenta",
  newer_invitation: "tiene una invitación más reciente (vigente o revocada)",
  supplier_removed: "el proveedor ya no existe",
  already_issued: "se le generó una invitación hace menos de un minuto",
};

export function skipReason(reason) {
  return SKIP_REASONS[reason] || reason;
}

export const REISSUE_DEFAULT_DAYS = 30;

// The preview line above the list of suppliers that would be re-invited (RF-9).
export function reissuePreviewText(result) {
  const { eligible, pending } = result.summary;
  if (eligible === 0) return "Ningún proveedor tiene una invitación vencida reciente sin cuenta.";
  const noun = eligible === 1 ? "proveedor" : "proveedores";
  const rest = pending > 0 ? ` Quedan ${pending} más para una nueva solicitud (el máximo es ${result.limit} por vez).` : "";
  return `Se reinvitaría a ${eligible} ${noun} con la invitación vencida en los últimos ${result.expired_within_days} días.${rest}`;
}

// The summary after confirming (RF-9).
export function reissueSummaryLines(result) {
  const s = result.summary;
  const lines = [`Reinvitados: ${s.reissued}`];
  if (s.emails_not_registered > 0) lines.push(`Sin correo registrado: ${s.emails_not_registered} (genera una invitación nueva a cada uno)`);
  if (s.skipped > 0) lines.push(`Omitidos: ${s.skipped}`);
  if (s.failed > 0) lines.push(`Con error: ${s.failed}`);
  if (s.pending > 0) lines.push(`Pendientes para una nueva solicitud: ${s.pending}`);
  return lines;
}
